import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../http.service';
import { CheckStatusRequest, CheckStatusResponse, GetPatientRequest, GetPatientResponse, PatientDetails, PatientStatus, RejectedStatusData, UpdatePatientContactRequest, UpdatePatientContactResponse, UpdatePatientMobileNumberRequest } from '../../models/patient.model';
import { PATHS } from '../../constants/paths';

const STORAGE_KEY = 'paacap_patient_details';

interface PatientDetailsState {
  patient: Partial<PatientDetails> | null;
  rejectedStatusData: RejectedStatusData | null;
  patient_id: number | null;
  registration_code: string | null;
  registration_status: PatientStatus | undefined;
}

const DEFAULT_PATIENT_DETAILS: PatientDetailsState = {
  patient: null,
  rejectedStatusData: null,
  patient_id: null,
  registration_code: null,
  registration_status: undefined,
};

@Injectable({
  providedIn: 'root'
})
export class PatientService {
  private httpService = inject(HttpService);
  private patientDetails: PatientDetailsState;

  constructor() {
    try {
      const registrationStatus = JSON.parse(localStorage.getItem(STORAGE_KEY)!).registration_status as PatientStatus;
      if(registrationStatus === 'UNREGISTERED') {
        this.patientDetails = { ...DEFAULT_PATIENT_DETAILS };
      } else {
        this.patientDetails = { ...DEFAULT_PATIENT_DETAILS, ...JSON.parse(localStorage.getItem(STORAGE_KEY)!) };
      }
    } catch {
      this.patientDetails = { ...DEFAULT_PATIENT_DETAILS };
    }
  }

  private savePatientDetails(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.patientDetails));
  }

  checkStatus(payload: CheckStatusRequest): Observable<CheckStatusResponse> {
    const params = { mobile_number: payload.mobile_number };
    return this.httpService.get<CheckStatusResponse>(PATHS.paacapPatientReg.checkStatus, params);
  }

  get(): Observable<GetPatientResponse> {
    return this.httpService.get<GetPatientResponse>(PATHS.paacapPatientReg.getPatient);
  }

  setCurrentPatient(patient: Partial<PatientDetails> | null): void {
    this.patientDetails.patient = patient;
    this.savePatientDetails();
  }

  getCurrentPatient(): Partial<PatientDetails> | null {
    return this.patientDetails.patient;
  }

  hasCurrentPatient(): boolean {
    return !!this.patientDetails.patient;
  }

  clearCurrentPatient(): void {
    this.patientDetails.patient = null;
    this.savePatientDetails();
  }

  setRejectedStatusData(data: RejectedStatusData | null): void {
    this.patientDetails.rejectedStatusData = data;
    this.savePatientDetails();
  }

  getRejectedStatusData(): RejectedStatusData | null {
    return this.patientDetails.rejectedStatusData;
  }

  clearRejectedStatusData(): void {
    this.patientDetails.rejectedStatusData = null;
    this.savePatientDetails();
  }

  clearAllData(): void {
    this.patientDetails.patient = null;
    this.patientDetails.rejectedStatusData = null;
    this.savePatientDetails();
  }

  setPatientId(patientId: number): void {
    this.patientDetails.patient_id = patientId;
    this.savePatientDetails();
  }

  getPatientId(): number | null {
    return this.patientDetails.patient_id;
  }

  setRegistrationCode(code: string): void {
    this.patientDetails.registration_code = code;
    this.savePatientDetails();
  }

  getRegistrationCode(): string {
    return this.patientDetails.registration_code || '';
  }

  setRegistrationStatus(status: PatientStatus): void {
    this.patientDetails.registration_status = status;
    this.savePatientDetails();
  }

  getRegistrationStatus(): PatientStatus | undefined {
    return this.patientDetails.registration_status;
  }

  clearSession(): void {
    this.patientDetails = { ...DEFAULT_PATIENT_DETAILS };
    localStorage.removeItem(STORAGE_KEY);
  }

  update(payload: UpdatePatientContactRequest): Observable<UpdatePatientContactResponse> {
    const { ...data } = payload;
    return this.httpService.post('/paacap-patient-reg/update', {
      registration_code: this.getRegistrationCode(),
      data
    });
  }

  updateMobileNumber(payload: UpdatePatientMobileNumberRequest): Observable<UpdatePatientContactResponse> {
    const { ...data } = payload;
    return this.httpService.post('/paacap-patient-reg/update_mobile', {
      registration_code: this.getRegistrationCode(),
      data
    });
  }

  updateCurrentPatientContact(updates: Partial<Pick<PatientDetails, 'email' | 'mobile_number'>>): void {
    if (this.patientDetails.patient) {
      this.patientDetails.patient = {
        ...this.patientDetails.patient,
        ...updates
      };
      this.savePatientDetails();
    }
  }
}
